import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from "../../Helpers/toastHelper";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faArrowLeft,
  faShieldAlt,
  faKey
} from "@fortawesome/free-solid-svg-icons";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    new_password: "",
    confirm_password: ""
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Invalid Link State
  if (!token || !email) {
    return (
      <div className="auth-page-container">
        <motion.div
          className="auth-card error-card"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="error-icon-wrapper">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h1>رابط غير صحيح</h1>
          <p>عذراً، الرابط الذي تحاول الوصول إليه غير صحيح أو منتهي الصلاحية.</p>

          <button className="action-btn" onClick={() => navigate("/forgot-password")}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>العودة لطلب رابط جديد</span>
          </button>
        </motion.div>
        <style jsx>{`
                    .auth-page-container {
                        min-height: 100vh;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        background: #f8fafc;
                          font-family: "Zain", sans-serif;
                        padding: 20px;
                        direction: rtl;
                    }
                    .auth-card {
                        background: white;
                        border-radius: 24px;
                        padding: 40px;
                        text-align: center;
                        box-shadow: 0 20px 60px rgba(0,0,0,0.08);
                        border: 1px solid rgba(0,0,0,0.05);
                        max-width: 480px;
                        width: 100%;
                    }
                    .error-icon-wrapper {
                        font-size: 4rem;
                        color: #ef4444;
                        margin-bottom: 20px;
                    }
                    h1 { 
                        color: #1e293b; 
                        margin-bottom: 10px; 
                        font-size: 1.8rem;
                    }
                    p {
                        color: #64748b;
                        margin-bottom: 30px;
                        line-height: 1.6;
                    }
                    .action-btn {
                        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
                        color: white;
                        border: none;
                        padding: 14px 24px;
                        border-radius: 12px;
                        font-weight: 700;
                        cursor: pointer;
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        font-family: inherit;
                        transition: 0.3s;
                    }
                    .action-btn:hover {
                         transform: translateY(-2px);
                         box-shadow: 0 10px 20px rgba(99, 102, 241, 0.2);
                    }
                `}</style>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password.length < 6) {
      toastHelper.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      toastHelper.error("كلمتا المرور غير متطابقتين");
      return;
    }

    setLoading(true);

    try {
      await ApiServices.post("User/ResetPassword", {
        token: token,
        email: email,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      });

      toastHelper.success("تم إعادة تعيين كلمة المرور بنجاح");
      navigate("/signin");
    } catch (err) {
      toastHelper.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <div className="background-animation">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <motion.div
        className="form-container"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="icon-badge">
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <FontAwesomeIcon icon={faShieldAlt} className="main-icon" />
          </motion.div>
          <div className="badge-notification">
            <FontAwesomeIcon icon={faKey} />
          </div>
        </div>

        <div className="text-content">
          <h1>تعيين كلمة مرور جديدة</h1>
          <p>قم بإنشاء كلمة مرور قوية لحماية حسابك</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* New Password */}
          <div className="input-group">
            <div className={`input-field ${focusedField === 'new' ? 'focused' : ''}`}>
              <FontAwesomeIcon icon={faLock} className="field-icon" />
              <input
                type={showPassword ? "text" : "password"}
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                onFocus={() => setFocusedField('new')}
                onBlur={() => setFocusedField(null)}
                placeholder="كلمة المرور الجديدة"
                required
              />
              <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="input-group">
            <div className={`input-field ${focusedField === 'confirm' ? 'focused' : ''}`}>
              <FontAwesomeIcon icon={faLock} className="field-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                onFocus={() => setFocusedField('confirm')}
                onBlur={() => setFocusedField(null)}
                placeholder="تأكيد كلمة المرور"
                required
              />
              <button type="button" className="eye-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          <button type="submit" className="submit-btn" disabled={loading || formData.new_password.length < 6}>
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <>
                <span>حفظ كلمة المرور</span>
                <FontAwesomeIcon icon={faCheckCircle} />
              </>
            )}
          </button>
        </form>

        <div className="footer-link">
          تذكرت كلمة المرور؟ <span onClick={() => navigate("/signin")}>تسجيل الدخول</span>
        </div>
      </motion.div>

      <style jsx>{`
        .reset-password-page {
            min-height: 100vh;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8fafc;
            position: relative;
            overflow: hidden;
            font-family: "Zain", sans-serif;
            direction: rtl;
            padding: 20px;
        }

        .background-animation {
            position: absolute;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
            z-index: 1;
        }

        .shape {
            position: absolute;
            border-radius: 50%;
            filter: blur(80px);
            opacity: 0.5;
            animation: float 20s infinite;
        }

        .shape-1 { width: 400px; height: 400px; background: #667eea; top: -100px; right: -100px; }
        .shape-2 { width: 300px; height: 300px; background: #764ba2; bottom: -50px; left: -50px; animation-delay: -5s; }
        .shape-3 { width: 200px; height: 200px; background: #f093fb; top: 40%; left: 40%; animation-delay: -10s; }

        @keyframes float {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            33% { transform: translate(30px, -50px) rotate(10deg); }
            66% { transform: translate(-20px, 20px) rotate(-5deg); }
        }

        .form-container {
            width: 100%;
            max-width: 480px;
            background: rgba(255,255,255,0.9);
            backdrop-filter: blur(20px);
            padding: 40px;
            border-radius: 24px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.08);
            border: 1px solid rgba(255,255,255,0.8);
            position: relative;
            z-index: 10;
            text-align: center;
        }

        .icon-badge {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 100%);
            border-radius: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 30px;
            position: relative;
        }

        .main-icon {
            font-size: 2.5rem;
            color: #6366f1;
        }

        .badge-notification {
            position: absolute;
            bottom: -5px;
            right: -5px;
            background: #ffffff;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 10px rgba(0,0,0,0.1);
            color: #6366f1;
            font-size: 0.9rem;
        }

        .text-content h1 {
            font-size: 1.8rem;
            font-weight: 800;
            color: #1e293b;
            margin-bottom: 12px;
        }

        .text-content p {
            color: #64748b;
            font-size: 1rem;
            line-height: 1.6;
            margin-bottom: 32px;
            max-width: 90%;
            margin-left: auto;
            margin-right: auto;
        }

        .input-group {
            text-align: right;
            margin-bottom: 24px;
        }

        .input-group label {
            display: block;
            font-weight: 700;
            font-size: 0.9rem;
            color: #334155;
            margin-bottom: 8px;
        }

        .input-field {
            display: flex;
            align-items: center;
            background: #ffffff;
            border: 1px solid #e2e8f0;
            padding: 0 16px;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(148, 163, 184, 0.05);
            transform: translateZ(0);
            width: 100%;
            box-sizing: border-box;
            overflow: hidden;
        }

        .input-field.focused {
            border-color: #6366f1;
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
            transform: translateY(-1px);
        }

        .field-icon {
            color: #94a3b8;
            font-size: 1.1rem;
            transition: 0.3s;
        }
        
        .input-field.focused .field-icon {
            color: #6366f1;
        }

        .input-field input {
            width: 100%;
            flex: 1;
            border: none;
            background: transparent;
            padding: 16px 12px;
            font-size: 1rem;
            color: #1e293b;
            outline: none;
            font-family: inherit;
        }

        .input-field input::placeholder {
            color: #cbd5e1;
        }

        /* Autofill Fix */
        .input-field input:-webkit-autofill,
        .input-field input:-webkit-autofill:hover, 
        .input-field input:-webkit-autofill:focus, 
        .input-field input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #ffffff inset !important;
            -webkit-text-fill-color: #1e293b !important;
            transition: background-color 5000s ease-in-out 0s;
        }

        .eye-btn {
            background: none; border: none;
            color: #94a3b8; cursor: pointer;
            padding: 8px; font-size: 0.95rem;
        }
        .eye-btn:hover { color: #334155; }

        .submit-btn {
            width: 100%;
            padding: 16px;
            background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
            color: white;
            border: none;
            border-radius: 16px;
            font-weight: 700;
            font-size: 1.1rem;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            transition: all 0.3s ease;
            box-shadow: 0 10px 20px rgba(99, 102, 241, 0.2);
            font-family: inherit;
        }

        .submit-btn:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 15px 30px rgba(99, 102, 241, 0.3);
        }

        .submit-btn:disabled {
            opacity: 0.7;
            cursor: wait;
        }

        .footer-link {
            margin-top: 32px;
            color: #64748b;
            font-size: 0.95rem;
        }

        .footer-link span {
            color: #6366f1;
            font-weight: 700;
            cursor: pointer;
            margin-right: 5px;
            transition: color 0.2s;
        }

        .footer-link span:hover {
            color: #4f46e5;
            text-decoration: underline;
        }

        @media(max-width: 480px) {
            .reset-password-page {
                padding: 16px;
            }
            .form-container {
                padding: 30px 20px;
            }
            .text-content h1 {
                font-size: 1.5rem;
            }
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;